<script>
    import { createEventDispatcher, onMount } from "svelte";
    import { preventDefault } from "svelte/legacy";
    import { format, locale } from "svelte-i18n";

    // Vanilla replacements
    import flatpickr from "flatpickr";
    import "flatpickr/dist/flatpickr.css";
    import { Spanish, English } from "flatpickr/dist/l10n/es.js";

    import TomSelect from "tom-select";
    import "tom-select/dist/css/tom-select.bootstrap5.css";

    import noUiSlider from "nouislider";
    import "nouislider/dist/nouislider.css";

    const dispatch = createEventDispatcher();

    let { config = { inputs: [], activeFilters: {} } } = $props();

    let filters = $state({});

    $effect(() => {
        let initial = JSON.parse(JSON.stringify(config.activeFilters || {}));

        for (let group of config.inputs || []) {
            for (let field of group) {
                // Map Select Arrays
                if (field.type === "multiple") {
                    if (!initial[field.name]) initial[field.name] = [];
                    else if (typeof initial[field.name] === "string") {
                        initial[field.name] = initial[field.name].split(",");
                    }
                }
                // Map Radios
                if (
                    field.type === "radio" &&
                    !initial.hasOwnProperty(field.name)
                ) {
                    initial[field.name] = "all";
                }
                if (
                    field.type === "select" &&
                    typeof initial[field.name] === "string"
                ) {
                    if (initial[field.name] === "true")
                        initial[field.name] = true;
                    if (initial[field.name] === "false")
                        initial[field.name] = false;
                }
                // Build internal date range string for Flatpickr
                if (field.type === "date") {
                    let gte = initial[`${field.name}__gte`];
                    let lte = initial[`${field.name}__lte`];
                    if (gte && lte) {
                        initial[field.name] =
                            `${gte.split("T")[0].replaceAll("-", "/")} - ${lte.split("T")[0].replaceAll("-", "/")}`;
                    }
                }
            }
        }
        filters = initial;
    });

    // --- Svelte Action 1: TomSelect (Replaces Select2 Pill Tags) ---
    function tomSelectAction(node, params) {
        let currentValue = JSON.stringify(params.value || []);

        const formatOptions = (opts) => {
            return (opts || []).map((opt) => {
                if (typeof opt === "object" && opt !== null) {
                    return {
                        id: opt.id,
                        name:
                            opt.name ||
                            opt.title_es ||
                            opt.title ||
                            opt.email ||
                            opt.id,
                    };
                }
                return { id: opt, name: opt };
            });
        };

        const ts = new TomSelect(node, {
            plugins: ["remove_button"],
            maxItems: null,
            valueField: "id",
            labelField: "name",
            searchField: "name",
            options: formatOptions(params.options),
            create: false,
            placeholder: params.placeholder || "Buscar...",
            onChange: (val) => {
                let formattedVal = !val ? [] : Array.isArray(val) ? val : [val];
                currentValue = JSON.stringify(formattedVal);
                params.onChange(formattedVal);
            },
        });

        if (params.value && params.value.length) {
            ts.setValue(params.value, true);
        }

        return {
            update(newParams) {
                params = newParams;
                const newString = JSON.stringify(newParams.value || []);
                if (newString !== currentValue) {
                    currentValue = newString;
                    ts.setValue(newParams.value || [], true);
                }
            },
            destroy() {
                ts.destroy();
            },
        };
    }

    // --- Svelte Action 2: Flatpickr (Replaces DateRangePicker Popup) ---
    function flatpickrRangeAction(node, params) {
        let currentValue = params.value || "";

        const fp = flatpickr(node, {
            mode: "range",
            dateFormat: "Y/m/d",
            locale: $locale == "es" ? Spanish : English,
            defaultDate: params.value ? params.value.split(" - ") : [],
            onChange: (selectedDates) => {
                // Force strict manual formatting to avoid Spanish " a " or English " to " strings
                if (selectedDates.length === 2) {
                    const formatDate = (d) => {
                        let y = d.getFullYear();
                        let m = String(d.getMonth() + 1).padStart(2, "0");
                        let day = String(d.getDate()).padStart(2, "0");
                        return `${y}/${m}/${day}`;
                    };

                    const internalValue = `${formatDate(selectedDates[0])} - ${formatDate(selectedDates[1])}`;
                    currentValue = internalValue;
                    params.onChange(internalValue);
                } else if (selectedDates.length === 0) {
                    currentValue = "";
                    params.onChange("");
                }
            },
            onClose: (selectedDates, dateStr, instance) => {
                if (selectedDates.length === 1) {
                    instance.clear();
                    currentValue = "";
                    params.onChange("");
                }
            },
        });

        return {
            update(newParams) {
                params = newParams;
                if (fp.isOpen) return;

                if (newParams.value === currentValue) return;
                currentValue = newParams.value || "";

                if (newParams.value) {
                    fp.setDate(newParams.value.split(" - "), false);
                } else {
                    fp.clear(false);
                }
            },
            destroy() {
                fp.destroy();
            },
        };
    }

    // --- Svelte Action 3: noUiSlider (Replaces ionRangeSlider) ---
    function noUiSliderAction(node, params) {
        let isUpdating = false;

        // Ensure absolute boundaries (0 -> arrays length, or explicit min/max)
        const getRange = (p) => {
            if (p.values && p.values.length)
                return { min: 0, max: p.values.length - 1 };
            return { min: Number(p.min ?? 0), max: Number(p.max ?? 100) };
        };

        // Determine where handles should sit
        const getStart = (p) => {
            if (p.value) return String(p.value).split(";"); // Pass exactly "5", "8"
            if (p.values && p.values.length)
                return [p.values[0], p.values[p.values.length - 1]];
            return [p.min ?? 0, p.max ?? 100]; // Snap exactly to edges if empty
        };

        let sliderOptions = {
            start: getStart(params),
            connect: true,
            tooltips: true,
            step: Number(params.step ?? 1),
            range: getRange(params),
        };

        // Map internal index to string values if 'values' array is provided
        if (params.values && params.values.length) {
            sliderOptions.format = {
                to: (v) => params.values[Math.round(v)],
                from: (v) => {
                    let idx = params.values.indexOf(v);
                    // Failsafe: if indexOf fails, find nearest numeric equivalent
                    if (idx === -1)
                        idx = params.values.findIndex(
                            (val) => parseFloat(val) === parseFloat(v),
                        );
                    return idx !== -1 ? idx : 0;
                },
            };
        } else {
            sliderOptions.format = {
                to: (v) => Number(v).toString(),
                from: (v) => Number(v),
            };
        }

        noUiSlider.create(node, sliderOptions);

        node.noUiSlider.on("change", (values) => {
            isUpdating = true;
            params.onChange(values.join(";")); // Always outputs "X;Y"
            setTimeout(() => (isUpdating = false), 150);
        });

        return {
            update(newParams) {
                params = newParams;
                if (!isUpdating) {
                    node.noUiSlider.set(getStart(newParams));
                }
            },
            destroy() {
                node.noUiSlider.destroy();
            },
        };
    }

    const handleSearch = () => {
        let payload = $state.snapshot(filters);

        // for (let group of config.inputs || []) {
        //     for (let field of group) {
        //         // Split date strings for parent config mapping
        //         if (field.type === "date") {
        //             let val = payload[field.name];
        //             if (val && val.includes(" - ")) {
        //                 let dates = val.split(" - ");
        //                 payload[`${field.name}__gte`] = dates[0].replaceAll("/", "-");
        //                 payload[`${field.name}__lte`] = dates[1].replaceAll("/", "-");
        //             }
        //             delete payload[field.name];
        //         }
        //     }
        // }
        dispatch("advancedSearch", payload);
    };

    const clearFilters = () => {
        filters = {};
        for (let group of config.inputs || []) {
            for (let field of group) {
                if (field.type === "multiple") filters[field.name] = [];
                if (field.type === "radio") filters[field.name] = "all";
            }
        }
        dispatch("advancedSearch", {});
    };

    const selectAllOptions = (field) => {
        filters[field.name] = field.options.map((opt) =>
            typeof opt === "object" ? opt.id : opt,
        );
    };

    const clearDateFilter = (field) => {
        filters[field.name] = "";
    };
</script>

<div class="advancedSearchWidget pb-2">
    {#each config.inputs as fieldgroup}
        <div class="row g-3 mb-4">
            {#each fieldgroup as field}
                <div class="col-12 col-lg">
                    <div
                        class="d-flex justify-content-between align-items-end mb-3"
                    >
                        <label
                            for={field.id}
                            class="form-label text-muted fw-bold mb-0"
                        >
                            {@html field.label}
                        </label>

                        <!-- Utility Links -->
                        {#if field.type === "multiple" && field.selectAll}
                            <a
                                href="#"
                                class="text-primary text-decoration-none"
                                style="font-size: 0.75rem;"
                                onclick={preventDefault(() =>
                                    selectAllOptions(field),
                                )}
                            >
                                {$format("select_all")}
                            </a>
                        {/if}
                        {#if field.type === "date"}
                            <a
                                href="#"
                                class="text-danger text-decoration-none"
                                style="font-size: 0.75rem;"
                                onclick={preventDefault(() =>
                                    clearDateFilter(field),
                                )}
                            >
                                {$format("clean")}
                            </a>
                        {/if}
                    </div>

                    <!-- Input Types -->
                    {#if field.type === "text" || field.type === "number"}
                        <input
                            type={field.type}
                            class="form-control form-control-sm standard-height"
                            id={field.id}
                            name={field.name}
                            placeholder={field.placeholder}
                            bind:value={filters[field.name]}
                            style="padding-top: 0.675rem !important; padding-bottom: 0.675rem !important;"
                        />

                        <!-- Select Multiple (TomSelect Pill Tags) -->
                    {:else if field.type === "multiple"}
                        <select
                            id={field.id}
                            name={field.name}
                            multiple
                            use:tomSelectAction={{
                                options: field.options,
                                value: filters[field.name],
                                placeholder: $format("buttons.search"),
                                onChange: (val) => (filters[field.name] = val),
                            }}
                        ></select>

                        <!-- Single Select -->
                    {:else if field.type === "select"}
                        <select
                            class="form-select form-select-sm standard-height"
                            id={field.id}
                            name={field.name}
                            bind:value={filters[field.name]}
                        >
                            <option value="">--</option>
                            {#each field.options as opt}
                                <option
                                    value={opt.id !== undefined ? opt.id : opt}
                                >
                                    {opt.name ||
                                        opt.title_es ||
                                        opt.title ||
                                        opt.email ||
                                        opt}
                                </option>
                            {/each}
                        </select>

                        <!-- Date Range Popup (Flatpickr Calendar) -->
                    {:else if field.type === "date"}
                        <div class="input-group input-group-sm standard-height">
                            <span class="input-group-text bg-light text-muted">
                                <i class="far fa-calendar-alt"></i>
                            </span>
                            <input
                                type="text"
                                class="form-control bg-white cursor-pointer"
                                id={field.id}
                                name={field.name}
                                placeholder="YYYY/MM/DD - YYYY/MM/DD"
                                readonly
                                use:flatpickrRangeAction={{
                                    value: filters[field.name],
                                    onChange: (val) =>
                                        (filters[field.name] = val),
                                }}
                            />
                        </div>

                        <!-- Dual Number Ranges Slider (noUiSlider) -->
                    {:else if field.type === "numrange"}
                        <div class="px-2 pt-4 pb-2 mt-4">
                            <div
                                use:noUiSliderAction={{
                                    min: field.min,
                                    max: field.max,
                                    step: field.step,
                                    values: field.values,
                                    value: filters[field.name],
                                    onChange: (val) =>
                                        (filters[field.name] = val),
                                }}
                            ></div>
                        </div>

                        <!-- Radios -->
                    {:else if field.type === "radio"}
                        <div
                            class="d-flex flex-wrap align-items-center standard-height gap-3"
                            style="height: 45px;"
                        >
                            {#each field.options as option}
                                <div class="form-check mb-0">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="{field.id}_{option.value}"
                                        name={field.name}
                                        value={option.value}
                                        bind:group={filters[field.name]}
                                    />
                                    <label
                                        class="form-check-label text-dark"
                                        for="{field.id}_{option.value}"
                                    >
                                        {option.label}
                                    </label>
                                </div>
                            {/each}
                        </div>
                    {/if}
                </div>
            {/each}
        </div>
    {/each}
</div>

<div class="btn-strip pt-3 mt-2 mb-4 border-top">
    <button class="btn btn-primary btn-sm px-4 me-2" onclick={handleSearch}>
        <i class="fas fa-search me-1"></i>
        {$format("buttons.search")}
    </button>
    <button class="btn btn-light btn-sm px-4 border" onclick={clearFilters}>
        <i class="fas fa-eraser me-1"></i>
        {$format("buttons.reset")}
    </button>
</div>

<style>
    a.text-danger,
    a.text-primary {
        position: relative;
        top: 88px;
        z-index: 9;
        right: 0px;
    }

    .cursor-pointer {
        cursor: pointer !important;
    }

    .form-check-input {
        margin-top: 0.25rem;
        cursor: pointer;
    }
    .form-check-label {
        cursor: pointer;
        padding-top: 2px;
    }

    :global(.flatpickr-input) {
        height: 45px;
    }

    /* Bootstrap 5 theme overrides for TomSelect tags */
    :global(.ts-control) {
        border-radius: 0.375rem !important;
        padding: 0.375rem 0.75rem !important;
        min-height: 38px !important;
        height: 46px;
    }

    :global(.ts-wrapper) {
        z-index: 100;
    }

    /* noUiSlider Overrides to match Bootstrap 5 */
    :global(.noUi-target) {
        background: #e9ecef;
        border: none;
        box-shadow: none;
        height: 6px;
    }
    :global(.noUi-connect) {
        background: #6c757d !important;
    }
    :global(.noUi-handle) {
        border-radius: 50%;
        background: #fff;
        border: 2px solid #6c757d !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        width: 18px !important;
        height: 18px !important;
        right: -9px !important;
        top: -6px !important;
        cursor: grab;
    }
    :global(.noUi-handle:before),
    :global(.noUi-handle:after) {
        display: none;
    }
    :global(.noUi-tooltip) {
        padding: 2px 6px;
        font-size: 0.75rem;
        border: none;
        background: #343a40;
        color: #fff;
        border-radius: 4px;
    }
    :global(.item[data-value="critical"]) {
        background-color: rgba(144, 70, 132, 0.9) !important;
        color: white !important;
    }
    :global(.item[data-value="high"]) {
        background-color: rgba(160, 33, 40, 0.9) !important;
        color: white !important;
    }
    :global(.item[data-value="medium"]) {
        background-color: rgba(212, 101, 47, 0.9) !important;
        color: white !important;
    }
    :global(.item[data-value="low"]) {
        background-color: rgba(247, 186, 11, 0.9) !important;
        color: white !important;
    }
    :global(.item[data-value="info"]) {
        background-color: #154889 !important;
        color: white !important;
    }
    :global(.ts-wrapper.plugin-remove_button:not(.rtl) .item .remove) {
        border-left: none !important;
        margin-left: 0;
    }
    :global(.ts-control input::placeholder) {
        color: white !important;
    }
    .form-select-sm {
        padding-top: 0.675rem;
        padding-bottom: 0.675rem;
    }
</style>
